import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { UseToastOptions } from '@chakra-ui/react';
import type { Influencer, MarketplaceProfileTurnaround } from '@foodfluence/shared';
import { marketplaceProfileApi } from '../../../../../services/api/marketplaceProfile';
import { uploadWithProgress } from '../../../../../utils/fileUtils';
import { BASE_HOST } from '../constants';
import { applyTopSlotMedia, reorderTopSlots } from '../lib/marketplaceProfileAssets';
import {
  associatedBrandsFromTopSlots,
  mergeMarketplaceBrands,
  normalizeMarketplaceBrandName,
} from '../lib/marketplaceProfileBrands';
import {
  buildInitialMarketplaceDefaults,
  hasCustomMarketplaceProfileImage,
  MARKETPLACE_BIO_MAX_CHARS,
} from '../lib/marketplaceProfileDefaults';
import {
  buildMarketplaceProfileUpsertDto,
  packagesFromProfileList,
} from '../lib/marketplaceProfileDto';
import { loadProfileImageFromRef, loadTopSlotsFromAssets } from '../lib/marketplaceProfileLoad';
import { reorderListItems } from '../lib/reorderListItems';
import type {
  MarketplaceFormSnapshot,
  MarketplaceProfilePatch,
  ProfileLoadPhase,
  UpsertProfileOptions,
} from '../lib/marketplaceProfileTypes';
import { pickAutoSavePatch } from '../lib/marketplaceProfileTypes';
import { getMarketplaceApiErrorMessage, validateMarketplaceSlugForSave } from '../lib/marketplaceProfileValidation';
import { isTurnaroundSelectionComplete, normalizeTurnaroundTime } from '../lib/turnaroundUtils';
import type { LocalCreatorsPackageRow, LocalCreatorsProfileImage, TopContentSlotEntry } from '../types';

type ToastFn = (options: UseToastOptions) => void;

const LC_SAVE_TOAST = {
  slug: 'local-creators-slug-saved',
  bio: 'local-creators-bio-saved',
  photo: 'local-creators-photo-saved',
  brands: 'local-creators-brands-saved',
  turnaround: 'local-creators-turnaround-saved',
  packages: 'local-creators-packages-saved',
  topContent: 'local-creators-top-content-saved',
} as const;

function showFieldSavedToast(toast: ToastFn, id: string, title: string) {
  toast({
    id,
    title,
    status: 'success',
    duration: 2500,
    isClosable: true,
  });
}

const EMPTY_SNAPSHOT: MarketplaceFormSnapshot = {
  bio: '',
  slug: '',
  turnaround: null,
  marketplaceBrands: [],
  packages: [],
  topSlots: [null, null, null],
  isAccountActive: false,
  profileImage: null,
};

export function useMarketplaceProfileForm(
  influencer: Partial<Influencer> | null | undefined,
  toast: ToastFn
) {
  const influencerId = influencer?.id;

  const [phase, setPhase] = useState<ProfileLoadPhase>('idle');
  const [slug, setSlug] = useState('');
  const [serverSlug, setServerSlug] = useState('');
  const [isSavingCreatorLink, setIsSavingCreatorLink] = useState(false);
  const slugTouchedRef = useRef(false);
  const [bio, setBio] = useState('');
  const [turnaround, setTurnaround] = useState<MarketplaceProfileTurnaround | null>(null);
  const [topSlots, setTopSlots] = useState<(TopContentSlotEntry | null)[]>([null, null, null]);
  const [marketplaceBrands, setMarketplaceBrands] = useState<string[]>([]);
  const marketplaceBrandsRef = useRef<string[]>([]);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [profileImage, setProfileImage] = useState<LocalCreatorsProfileImage | null>(null);
  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const [packages, setPackages] = useState<LocalCreatorsPackageRow[]>([]);
  const [isAccountActive, setIsAccountActive] = useState(false);

  const formSnapshotRef = useRef<MarketplaceFormSnapshot>(EMPTY_SNAPSHOT);
  const welcomeAutoOpenedRef = useRef(false);
  const influencerRef = useRef(influencer);
  influencerRef.current = influencer;
  const [hasLoadedProfileMeta, setHasLoadedProfileMeta] = useState(false);

  const filledCount = topSlots.filter(Boolean).length;

  const missingForActivation = useMemo(() => {
    const m: string[] = [];
    const trimmedBio = bio.trim();
    if (trimmedBio.length < 1 || trimmedBio.length > MARKETPLACE_BIO_MAX_CHARS) {
      m.push(`a public bio (up to ${MARKETPLACE_BIO_MAX_CHARS} characters)`);
    }
    if (slug.trim() === '') m.push('your page path');
    if (!isTurnaroundSelectionComplete(turnaround)) {
      m.push('a turnaround time');
    }
    if (filledCount < 3) m.push('at least 3 top content items (up to 5)');
    if (packages.length < 1) m.push('at least one package');
    return m;
  }, [bio, slug, turnaround, filledCount, packages.length]);

  const canActivate = missingForActivation.length === 0;

  const publicUrl = useMemo(
    () => `https://${BASE_HOST.replace(/\/$/, '')}/${slug.replace(/^\/+/, '')}`,
    [slug]
  );

  const markSlugTouched = useCallback(() => {
    slugTouchedRef.current = true;
  }, []);

  useEffect(() => {
    marketplaceBrandsRef.current = marketplaceBrands;
  }, [marketplaceBrands]);

  useLayoutEffect(() => {
    formSnapshotRef.current = {
      bio,
      slug,
      turnaround,
      marketplaceBrands,
      packages,
      topSlots,
      isAccountActive,
      profileImage,
    };
  });

  const upsertProfile = useCallback(
    async (patch: MarketplaceProfilePatch, opts?: UpsertProfileOptions): Promise<boolean> => {
      if (!influencerId) {
        return false;
      }

      if ('slug' in patch && patch.slug !== undefined) {
        const t = patch.slug.trim();
        if (t !== '') {
          const slugCheck = validateMarketplaceSlugForSave(patch.slug);
          if (slugCheck.valid === false) {
            if (!opts?.quietSlugValidation) {
              toast({
                title: slugCheck.title,
                description: slugCheck.description,
                status: 'error',
                duration: 5000,
                isClosable: true,
              });
            }
            return false;
          }
        }
      }

      const dto = buildMarketplaceProfileUpsertDto(patch);
      if (Object.keys(dto).length === 0) {
        return false;
      }

      const bioOnly = 'bio' in patch && Object.keys(patch).length === 1;

      try {
        await marketplaceProfileApi.upsert(influencerId, dto);
        return true;
      } catch (e) {
        console.error('Error saving marketplace profile:', e);
        toast({
          title: bioOnly ? 'Could not save bio' : 'Could not save',
          description: getMarketplaceApiErrorMessage(e, 'Check your connection and try again.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw e;
      }
    },
    [influencerId, toast]
  );

  const patchAndSave = useCallback(
    async (
      apply: () => void,
      rollback: () => void,
      patch: MarketplaceProfilePatch
    ) => {
      apply();
      try {
        await upsertProfile(patch);
      } catch {
        rollback();
        throw new Error('__PATCH_SAVE_FAILED__');
      }
    },
    [upsertProfile]
  );

  const updateBrands = useCallback(
    (next: string[]) => {
      marketplaceBrandsRef.current = next;
      setMarketplaceBrands(next);
    },
    []
  );

  const saveCreatorLink = useCallback(async (): Promise<boolean> => {
    if (slug.trim() === serverSlug) {
      return true;
    }
    setIsSavingCreatorLink(true);
    try {
      const ok = await upsertProfile({ slug: slug.trim() });
      if (ok) {
        setServerSlug(slug.trim());
        showFieldSavedToast(toast, LC_SAVE_TOAST.slug, 'Creator link saved');
      }
      return ok;
    } catch {
      return false;
    } finally {
      setIsSavingCreatorLink(false);
    }
  }, [slug, serverSlug, toast, upsertProfile]);

  const saveBio = useCallback(async (): Promise<boolean> => {
    if (!influencerId) {
      return false;
    }
    const bioTrim = bio.trim();
    if (bioTrim.length > MARKETPLACE_BIO_MAX_CHARS) {
      toast({
        title: 'Bio is too long',
        description: `Public bio can be at most ${MARKETPLACE_BIO_MAX_CHARS} characters. Shorten the text, then try again.${
          isAccountActive
            ? ' Your live page will keep the previous saved bio until you save a shorter one.'
            : ''
        }`,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return false;
    }
    setIsSavingBio(true);
    try {
      const ok = await upsertProfile({ bio });
      if (ok) {
        showFieldSavedToast(toast, LC_SAVE_TOAST.bio, 'Bio saved');
      }
      return ok;
    } catch {
      return false;
    } finally {
      setIsSavingBio(false);
    }
  }, [bio, influencerId, isAccountActive, toast, upsertProfile]);

  const deactivateAccount = useCallback(
    async (reason: string) => {
      if (!influencerId) return;
      await upsertProfile({ isAccountActive: false, deactivationReason: reason });
      setIsAccountActive(false);
      toast({
        id: 'local-creators-visibility',
        title: 'Account deactivated',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    },
    [influencerId, toast, upsertProfile]
  );

  const activateAccount = useCallback(async () => {
    if (!canActivate) {
      toast({
        id: 'local-creators-activate-incomplete',
        title: "Profile isn't complete",
        description: `Add ${missingForActivation.join(', ')} to activate your page.`,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    setIsAccountActive(true);
    try {
      await upsertProfile({ isAccountActive: true });
      toast({
        id: 'local-creators-visibility',
        title: 'Account active',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch {
      setIsAccountActive(false);
    }
  }, [canActivate, missingForActivation, toast, upsertProfile]);

  const handleAddMarketplaceBrand = useCallback(
    async (brandName: string) => {
      if (!influencerId) return;
      const trimmed = normalizeMarketplaceBrandName(brandName);
      if (!trimmed) return;

      const prev = marketplaceBrandsRef.current;
      if (prev.some((b) => b.toLowerCase() === trimmed.toLowerCase())) {
        toast({
          title: 'Already added',
          status: 'warning',
          duration: 2000,
          isClosable: true,
        });
        throw new Error('__BRAND_DUPLICATE__');
      }
      const next = [...prev, trimmed];
      await patchAndSave(
        () => updateBrands(next),
        () => updateBrands(prev),
        { marketplaceBrands: next }
      );
      showFieldSavedToast(toast, LC_SAVE_TOAST.brands, 'Brand added');
    },
    [influencerId, patchAndSave, toast, updateBrands]
  );

  const handleRemoveBrand = useCallback(
    async (brandName: string) => {
      if (!influencerId) return;
      const prev = marketplaceBrandsRef.current;
      const next = prev.filter((b) => b !== brandName);
      try {
        await patchAndSave(
          () => updateBrands(next),
          () => updateBrands(prev),
          { marketplaceBrands: next }
        );
        showFieldSavedToast(toast, LC_SAVE_TOAST.brands, 'Brand removed');
      } catch (error) {
        console.error('Error removing brand:', error);
        toast({
          title: 'Error removing brand',
          description: getMarketplaceApiErrorMessage(error, 'Please try again'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [influencerId, patchAndSave, toast, updateBrands]
  );

  const uploadProfilePhoto = useCallback(
    async (file: File) => {
      if (!influencerId) {
        throw new Error('Not signed in');
      }
      setIsUploadingProfileImage(true);
      try {
        const assetId = crypto.randomUUID();
        const storagePath = `marketplaceCreatorProfiles/${influencerId}/profile/${assetId}.jpg`;
        const url = await uploadWithProgress(file, storagePath, () => {});
        const next: LocalCreatorsProfileImage = {
          id: assetId,
          storagePath,
          url,
          contentType: file.type || 'image/jpeg',
        };
        setProfileImage(next);
        const ok = await upsertProfile({ profileImage: next });
        if (ok) {
          showFieldSavedToast(toast, LC_SAVE_TOAST.photo, 'Profile photo updated');
        }
      } catch (e) {
        console.error('Error uploading profile image:', e);
        toast({
          title: 'Could not update photo',
          description: getMarketplaceApiErrorMessage(e, 'Check your connection and try again.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        throw e;
      } finally {
        setIsUploadingProfileImage(false);
      }
    },
    [influencerId, toast, upsertProfile]
  );

  const clearMarketplaceProfileImage = useCallback(async () => {
    const prev = profileImage;
    setProfileImage(null);
    try {
      const ok = await upsertProfile({ profileImage: null });
      if (ok) {
        showFieldSavedToast(toast, LC_SAVE_TOAST.photo, 'Using your account photo');
      }
    } catch {
      setProfileImage(prev);
    }
  }, [profileImage, toast, upsertProfile]);

  const saveTopSlotsPatch = useCallback(
    (next: (TopContentSlotEntry | null)[], brandsNext: string[]) => {
      queueMicrotask(() => {
        void upsertProfile({ topSlots: next, marketplaceBrands: brandsNext });
      });
    },
    [upsertProfile]
  );

  const handleMarketplaceTopUpload = useCallback(
    async (file: File, brandName: string, caption: string, slotIndex: number) => {
      if (!influencerId || slotIndex < 0) {
        return;
      }
      const isVideo = file.type.startsWith('video/');
      const ext = (file.name.split('.').pop() || (isVideo ? 'mp4' : 'jpg')).toLowerCase();
      const assetId = crypto.randomUUID();
      const storagePath = `marketplaceCreatorProfiles/${influencerId}/top/${assetId}.${ext}`;
      const url = await uploadWithProgress(file, storagePath, () => {});
      const trimmedBrand = normalizeMarketplaceBrandName(brandName);
      const trimmedCaption = caption.trim();

      setTopSlots((prev) => {
        const entry: TopContentSlotEntry = {
          id: assetId,
          storagePath,
          url,
          isVideo,
          contentType: file.type || undefined,
          ...(trimmedBrand ? { associatedBrand: trimmedBrand } : {}),
          ...(trimmedCaption ? { caption: trimmedCaption } : {}),
        };
        const next = applyTopSlotMedia(prev, slotIndex, entry);
        const brandsNext = trimmedBrand
          ? mergeMarketplaceBrands(marketplaceBrandsRef.current, trimmedBrand)
          : marketplaceBrandsRef.current;
        if (brandsNext.length !== marketplaceBrandsRef.current.length) {
          updateBrands(brandsNext);
        }
        saveTopSlotsPatch(next, brandsNext);
        return next;
      });
    },
    [influencerId, saveTopSlotsPatch, updateBrands]
  );

  const handleMarketplaceTopMetaUpdate = useCallback(
    (brandName: string, caption: string, slotIndex: number) => {
      if (slotIndex < 0) return;
      const trimmedBrand = normalizeMarketplaceBrandName(brandName);
      const trimmedCaption = caption.trim();
      setTopSlots((prev) => {
        const slot = prev[slotIndex];
        if (!slot) return prev;
        const next = [...prev];
        next[slotIndex] = {
          ...slot,
          associatedBrand: trimmedBrand || undefined,
          caption: trimmedCaption || undefined,
        };
        const brandsNext = trimmedBrand
          ? mergeMarketplaceBrands(marketplaceBrandsRef.current, trimmedBrand)
          : marketplaceBrandsRef.current;
        if (brandsNext.length !== marketplaceBrandsRef.current.length) {
          updateBrands(brandsNext);
        }
        saveTopSlotsPatch(next, brandsNext);
        return next;
      });
    },
    [saveTopSlotsPatch, updateBrands]
  );

  const handleReorderTopSlots = useCallback(
    (fromIndex: number, toIndex: number) => {
      setTopSlots((prev) => {
        const next = reorderTopSlots(prev, fromIndex, toIndex);
        const unchanged =
          next.length === prev.length && next.every((slot, i) => slot === prev[i]);
        if (unchanged) {
          return prev;
        }
        saveTopSlotsPatch(next, marketplaceBrandsRef.current);
        showFieldSavedToast(toast, LC_SAVE_TOAST.topContent, 'Content order saved');
        return next;
      });
    },
    [saveTopSlotsPatch, toast]
  );

  const savePackages = useCallback(
    (next: LocalCreatorsPackageRow[]) => {
      setPackages(next);
      void upsertProfile({ packages: next }).then((ok) => {
        if (ok) {
          showFieldSavedToast(toast, LC_SAVE_TOAST.packages, 'Packages saved');
        }
      });
    },
    [toast, upsertProfile]
  );

  const handleReorderPackages = useCallback(
    (fromIndex: number, toIndex: number) => {
      setPackages((prev) => {
        const next = reorderListItems(prev, fromIndex, toIndex);
        const unchanged = next.every((pkg, i) => pkg === prev[i]);
        if (unchanged) {
          return prev;
        }
        void upsertProfile({ packages: next }).then((ok) => {
          if (ok) {
            showFieldSavedToast(toast, LC_SAVE_TOAST.packages, 'Packages saved');
          }
        });
        return next;
      });
    },
    [toast, upsertProfile]
  );

  const saveTurnaround = useCallback(
    (next: MarketplaceProfileTurnaround | null) => {
      setTurnaround(next);
      void upsertProfile({ turnaround: next }).then((ok) => {
        if (ok) {
          showFieldSavedToast(toast, LC_SAVE_TOAST.turnaround, 'Turnaround time saved');
        }
      });
    },
    [toast, upsertProfile]
  );

  const bootstrapStartedRef = useRef(false);

  // Load profile when influencer changes
  useEffect(() => {
    if (!influencerId || !influencerRef.current) {
      setPhase('idle');
      setHasLoadedProfileMeta(false);
      setMarketplaceBrands([]);
      welcomeAutoOpenedRef.current = false;
      bootstrapStartedRef.current = false;
      slugTouchedRef.current = false;
      return;
    }

    let cancelled = false;
    welcomeAutoOpenedRef.current = false;
    bootstrapStartedRef.current = false;
    slugTouchedRef.current = false;
    setHasLoadedProfileMeta(false);
    setPhase('loading');

    (async () => {
      try {
        const res = await marketplaceProfileApi.getMarketplaceProfile(influencerId);
        if (cancelled) return;

        const p = res.profile;
        if (p) {
          if (p.bio) setBio(p.bio);
          if (p.slug) setSlug(p.slug);
          setServerSlug((p.slug ?? '').trim());
          setTurnaround(
            normalizeTurnaroundTime(
              p.turnaroundTime,
              (p as { turnaroundTimeCustom?: { amount: number; unit: 'days' | 'weeks' } })
                .turnaroundTimeCustom
            )
          );
          setPackages(packagesFromProfileList(p.packages));
          setIsAccountActive(p.status === 'published');
          updateBrands(p.brands ?? []);
          setHasLoadedProfileMeta(true);

          const [slots, profImg] = await Promise.all([
            loadTopSlotsFromAssets(p.assets),
            loadProfileImageFromRef(p.profileImage),
          ]);
          if (cancelled) return;

          const mergedBrands = mergeMarketplaceBrands(
            p.brands ?? [],
            ...associatedBrandsFromTopSlots(slots)
          );
          setTopSlots(slots);
          updateBrands(mergedBrands);
          setProfileImage(profImg);
          setPhase('ready');
        } else {
          setServerSlug('');
          setPackages([]);
          setIsAccountActive(false);
          setTurnaround(null);

          if (!slugTouchedRef.current) {
            const defaults = buildInitialMarketplaceDefaults(influencerRef.current as Influencer);
            if (defaults.slug) setSlug(defaults.slug);
            if (defaults.profileImage) setProfileImage(defaults.profileImage);
          } else {
            setProfileImage(null);
          }
          updateBrands([]);
          setHasLoadedProfileMeta(true);
          setPhase('new');
        }
      } catch (e) {
        console.error('Error loading marketplace profile:', e);
        if (!cancelled) {
          toast({
            title: 'Could not load marketplace profile',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setHasLoadedProfileMeta(true);
          setPhase('idle');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [influencerId, toast, updateBrands]);

  // Bootstrap defaults for new profiles (background; do not block brands UI)
  useEffect(() => {
    if (phase !== 'new' || !influencerId || !influencerRef.current || bootstrapStartedRef.current) {
      return;
    }
    bootstrapStartedRef.current = true;

    let cancelled = false;

    (async () => {
      const defaults = buildInitialMarketplaceDefaults(influencerRef.current as Influencer);
      const slugCandidate = (
        slugTouchedRef.current ? formSnapshotRef.current.slug : defaults.slug ?? ''
      ).trim();
      const profileImageCandidate = defaults.profileImage ?? formSnapshotRef.current.profileImage;
      const hasSlug = slugCandidate !== '';
      const hasProfileImage = Boolean(profileImageCandidate);

      if (!hasSlug && !hasProfileImage) {
        if (!cancelled) setPhase('ready');
        return;
      }

      const overrides: MarketplaceProfilePatch = {};
      if (hasSlug && !slugTouchedRef.current) overrides.slug = slugCandidate;
      if (hasProfileImage) overrides.profileImage = profileImageCandidate ?? null;

      try {
        if (hasSlug) {
          const saved = await upsertProfile(overrides, { quietSlugValidation: true });
          if (saved) {
            setServerSlug(slugCandidate);
          } else if (hasProfileImage && profileImageCandidate) {
            await upsertProfile({ profileImage: profileImageCandidate });
          }
        } else if (hasProfileImage && profileImageCandidate) {
          await upsertProfile({ profileImage: profileImageCandidate });
        }
      } catch {
        // Debounced auto-save can still create the doc if bootstrap fails.
      } finally {
        if (!cancelled) {
          setPhase('ready');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [phase, influencerId, upsertProfile]);

  // Debounced auto-save
  useEffect(() => {
    if (phase !== 'ready' || !influencerId) {
      return;
    }
    const t = setTimeout(() => {
      void upsertProfile(pickAutoSavePatch(formSnapshotRef.current));
    }, 1200);
    return () => clearTimeout(t);
  }, [
    phase,
    influencerId,
    bio,
    turnaround,
    marketplaceBrands,
    packages,
    topSlots,
    profileImage,
    upsertProfile,
  ]);

  const shouldShowWelcome = phase === 'new';

  const markWelcomeShown = useCallback(() => {
    welcomeAutoOpenedRef.current = true;
  }, []);

  const isWelcomePending = shouldShowWelcome && !welcomeAutoOpenedRef.current;

  return {
    phase,
    isProfileLoading: phase === 'loading' && !hasLoadedProfileMeta,
    isWelcomePending,
    markWelcomeShown,
    bio,
    setBio,
    slug,
    setSlug,
    markSlugTouched,
    serverSlug,
    turnaround,
    topSlots,
    setTopSlots,
    marketplaceBrands,
    packages,
    setPackages,
    profileImage,
    isAccountActive,
    isSavingBio,
    isSavingCreatorLink,
    isUploadingProfileImage,
    filledCount,
    canActivate,
    missingForActivation,
    publicUrl,
    hasCustomProfileImage: hasCustomMarketplaceProfileImage(
      profileImage,
      influencer?.profile_pic_url
    ),
    saveBio,
    saveCreatorLink,
    upsertProfile,
    deactivateAccount,
    activateAccount,
    handleAddMarketplaceBrand,
    handleRemoveBrand,
    uploadProfilePhoto,
    clearMarketplaceProfileImage,
    handleMarketplaceTopUpload,
    handleMarketplaceTopMetaUpdate,
    handleReorderTopSlots,
    handleReorderPackages,
    savePackages,
    saveTurnaround,
  };
}
